/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from "react";
import { compose } from "recompose";
import { History } from "history";
import { withRouter } from "react-router";
import { observer, inject } from "mobx-react";
// UI Frameworks
import { Form, Row, Col, Radio, Icon } from "antd";
import { FormComponentProps } from "antd/lib/form";
// Shared components
import Button from "components/Button";
import Box from "components/Box";
import Maybe from "components/Maybe";
import InputNumber from "components/InputNumber";
// Local Components
import InsufficientBalance from "../InsufficientBalance";
// Hooks and Utilities
import { useIssue } from "../..";
import UserStore from "state/user";
import { debounce, get } from "lodash";
import { isFloat } from "helpers/index";
import addCommas from "helpers/index";
import { persianNumber } from "helpers/persian";
// styles
import styles from "./index.module.scss";

interface IProps extends FormComponentProps {
	user: UserStore;
	history: History;
}

const FirstStep: React.FC<IProps> = (props: IProps) => {
	const { state, dispatch } = useIssue();
	const [showModal, setShowModal] = React.useState(false);
	const [selectedRadio, setSelectedRadio] = React.useState("unit");
	const { getFieldDecorator, getFieldValue, setFieldsValue } = props.form;
	const buyPrice = props.user.goldBuyPrice;

	React.useEffect(() => {
		setFieldsValue({
			unit: get(props.history.location, ["query", "unit"]) || state.unit,
			amount: Math.round(state.unit * props.user.goldBuyPrice),
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state.unit, props.user.goldBuyPrice]);

	const [debouncedFunction] = useDebouncedCallback((value: any) => {
		const price = props.user.goldBuyPrice;
		const unit = (value / price).toFixed(4);

		// round the amount
		setFieldsValue({
			amount: Math.round(Number(unit) * price),
		});
	}, 1000);

	function useDebouncedCallback(callback: (value: any) => void, delay: number) {
		const d = callback;

		const callbackfunc = React.useCallback(debounce(d, delay), []);

		return [callbackfunc];
	}

	function issueDiffAmount(unit: number) {
		const { balance, goldBuyPrice } = props.user;
		const diff = Math.ceil(balance - unit * goldBuyPrice);
		return diff;
	}

	function handleSubmit(e: React.FormEvent): void {
		e.preventDefault();
		props.form.validateFields(async (err, { unit }) => {
			if (!err) {
				const diff = issueDiffAmount(unit);
				if (diff >= 0) {
					// go to next step
					dispatch({
						unit,
						current: 1,
						buyPrice: props.user.goldBuyPrice,
					});
				} else {
					// Make positive diff amount
					dispatch({ unit, diff: Math.abs(diff) });
				}
				setShowModal(true);
				return;
			}
		});
	}

	function handleUnitValidator(_: any, value: string, callback: any) {
		if (selectedRadio === "unit") {
			if (!value) {
				callback("لطفا مقدار این فیلد را وارد کنید.");
			} else if (value && !Number(value)) {
				callback("لطفا مقدار عددی وارد کنید.");
			} else if (Number(value) < 0) {
				callback("مقدار منفی قابل قبول نیست.");
			} else {
				callback();
			}

			return;
		}

		callback();
	}

	function handleAmountValidator(_: any, value: string, callback: any) {
		if (selectedRadio === "amount") {
			if (!value) {
				callback("لطفا مقدار این فیلد را وارد کنید.");
			} else if (value && !Number(value)) {
				callback("لطفا مقدار عددی وارد کنید.");
			} else if (isFloat(value)) {
				callback("لطفا مقدار اعشاری وارد نکنید.");
				return;
			} else if (Number(value) < 0 || Number(value) < 1000) {
				callback("مقدار قابل قبول نیست.");
			} else {
				callback();
			}

			return;
		}

		callback();
	}

	function onCloseModal() {
		setShowModal(false);
	}

	return (
		<div className={styles["first-step"]}>
			<Row className={styles["issue-Info"]} type="flex" justify="center">
				<Col md={10} xs={24}>
					<Box title="قیمت خرید طلا در سامانه" prefix="ریال">
						{addCommas(props.user.goldBuyPrice)}
					</Box>
				</Col>
				<Col md={10} xs={24}>
					<Box title="دارایی های شما در سامانه" gray>
						<Row type="flex" justify="center" style={{ marginBottom: "10px" }}>
							<Col span={10}>موجودی طلا</Col>
							<Col span={10}>موجودی ریالی</Col>
						</Row>
						<Row type="flex" justify="center" style={{ marginBottom: "10px" }}>
							<Col span={10}>{props.user.goldCredit} گرم</Col>
							<Col span={10}>{addCommas(props.user.balance)} ریال</Col>
						</Row>
					</Box>
				</Col>
			</Row>
			<Row type="flex" justify="center">
				<Col md={12} xs={24}>
					<Form onSubmit={handleSubmit}>
						<Form.Item>
							<Radio.Group
								name="radiogroup"
								value={selectedRadio}
								onChange={e => {
									const { value } = e.target;
									setSelectedRadio(value);
									setTimeout(() => {
										props.form.validateFields(["unit", "amount"], { force: true });
									}, 0);
								}}
							>
								<Form.Item>
									<Radio value="unit">
										<span style={{ display: "inline-block", textAlign: "right", width: 100 }}>
											طلای درخواستی
										</span>
									</Radio>
									{getFieldDecorator("unit", {
										rules: [
											{
												validator: handleUnitValidator,
											},
										],
									})(
										<InputNumber
											step="0.004"
											autoComplete="off"
											formatter={(value: any) =>
												persianNumber(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
											}
											parser={(value: any) => persianNumber(value).replace(/\$\s?|(,*)/g, "")}
											disabled={selectedRadio === "amount" || buyPrice === null}
											onChange={(val: any) => {
												const value = Number(val);
												if (value < 0) {
													return;
												}

												const amount = Math.round(value * props.user.goldBuyPrice);
												setFieldsValue({
													amount,
												});
											}}
										/>,
									)}
									<span style={{ marginRight: 10 }}>گرم</span>
								</Form.Item>
								<Form.Item>
									<Radio value="amount">
										<span style={{ display: "inline-block", textAlign: "right", width: 100 }}>
											مبلغ پرداختی
										</span>
									</Radio>
									{getFieldDecorator("amount", {
										rules: [
											{
												validator: handleAmountValidator,
											},
										],
									})(
										<InputNumber
											autoComplete="off"
											formatter={(value: any) =>
												persianNumber(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
											}
											parser={(value: any) => persianNumber(value).replace(/\$\s?|(,*)/g, "")}
											disabled={selectedRadio === "unit" || buyPrice === null}
											onChange={(val: any) => {
												const value = Number(val);
												if (value < 0) {
													return;
												}

												const unit = (value / props.user.goldBuyPrice).toFixed(4);
												debouncedFunction(value);
												setFieldsValue({
													unit,
												});
											}}
										/>,
									)}
									<span style={{ marginRight: 10 }}>ریال</span>
								</Form.Item>
							</Radio.Group>
						</Form.Item>

						<Maybe condition={state.current >= 1}>
							<Button onClick={() => dispatch({ current: state.current - 1 })} primary>
								بازگشت
							</Button>
						</Maybe>

						<Form.Item className="button-center">
							<Button htmlType="submit" disabled={buyPrice === null} green>
								ادامه درخواست
								<Icon type="arrow-left" style={{ verticalAlign: "middle", marginRight: "8px" }} />
							</Button>
						</Form.Item>
					</Form>
					<InsufficientBalance
						user={props.user}
						diff={state.diff + 10000} // +10,000 IRR
						shouldVisible={showModal}
						onClose={onCloseModal}
						unit={getFieldValue("unit")}
					/>
				</Col>
			</Row>
		</div>
	);
};

const enhance = compose<IProps, {}>(withRouter, Form.create({ name: "FirstStep" }), inject("user"), observer);

export default enhance(FirstStep);
